import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingHistory = ({ currentPlan }) => {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [showDetails, setShowDetails] = useState({});
  
  const billingHistory = [
    {
      id: 'inv-001',
      date: '2025-09-07',
      plan: 'professional',
      amount: 29.00,
      status: 'paid',
      period: '7 Sept - 7 Oct 2025',
      paymentMethod: 'Visa ****1234',
      downloadUrl: '#'
    },
    {
      id: 'inv-002',
      date: '2025-08-07',
      plan: 'professional',
      amount: 29.00,
      status: 'paid',
      period: '7 Août - 7 Sept 2025',
      paymentMethod: 'Visa ****1234',
      downloadUrl: '#'
    },
    {
      id: 'inv-003',
      date: '2025-07-07',
      plan: 'professional',
      amount: 29.00,
      status: 'paid',
      period: '7 Juillet - 7 Août 2025',
      paymentMethod: 'Visa ****1234',
      downloadUrl: '#'
    },
    {
      id: 'inv-004',
      date: '2025-06-07',
      plan: 'free',
      amount: 0.00,
      status: 'free',
      period: '7 Juin - 7 Juillet 2025',
      paymentMethod: 'Plan gratuit',
      downloadUrl: null
    },
    {
      id: 'inv-005',
      date: '2025-05-15',
      plan: 'professional',
      amount: 29.00,
      status: 'refunded',
      period: '15 Mai - 7 Juin 2025',
      paymentMethod: 'Visa ****1234',
      downloadUrl: '#',
      refundReason: 'Annulation dans les 30 jours'
    }
  ];

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })?.format(new Date(dateString));
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'Payé',
        className: 'bg-success/10 text-success',
        icon: 'CheckCircle'
      },
      pending: {
        label: 'En attente',
        className: 'bg-warning/10 text-warning',
        icon: 'Clock'
      },
      failed: {
        label: 'Échec',
        className: 'bg-error/10 text-error',
        icon: 'XCircle'
      },
      refunded: {
        label: 'Remboursé',
        className: 'bg-muted text-muted-foreground',
        icon: 'RotateCcw'
      },
      free: {
        label: 'Gratuit',
        className: 'bg-muted text-muted-foreground',
        icon: 'Gift'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {config?.label}
      </span>
    );
  };

  const getPlanName = (plan) => {
    const planNames = {
      free: 'Artiste',
      professional: 'Professionnel',
      enterprise: 'Entreprise'
    };
    return planNames?.[plan] || plan;
  };

  const toggleDetails = (invoiceId) => {
    setShowDetails(prev => ({
      ...prev,
      [invoiceId]: !prev?.[invoiceId]
    }));
  };

  const handleDownload = (invoice) => {
    console.log('Downloading invoice:', invoice?.id);
    // Here you would handle the invoice download
  };

  const totalSpent = billingHistory
    ?.filter(invoice => invoice?.status === 'paid')
    ?.reduce((sum, invoice) => sum + invoice?.amount, 0);

  const availableYears = [...new Set(billingHistory?.map(invoice => 
    new Date(invoice?.date).getFullYear()
  ))];

  const filteredHistory = billingHistory?.filter(invoice => 
    new Date(invoice?.date)?.getFullYear() === selectedYear
  );

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">
            Historique de facturation
          </h3>
          <p className="text-sm text-muted-foreground">
            Consultez et téléchargez vos factures
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e?.target?.value))}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {availableYears?.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            iconSize={14}
            onClick={() => console.log('Export all invoices')}
          >
            Exporter tout
          </Button>
        </div>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="DollarSign" size={20} className="text-success" />
            <div>
              <p className="text-sm text-muted-foreground">Total dépensé</p>
              <p className="text-lg font-semibold text-card-foreground">
                {formatAmount(totalSpent)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Receipt" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Factures {selectedYear}</p>
              <p className="text-lg font-semibold text-card-foreground">
                {filteredHistory?.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="Crown" size={20} className="text-accent" />
            <div>
              <p className="text-sm text-muted-foreground">Plan actuel</p>
              <p className="text-lg font-semibold text-card-foreground">
                {getPlanName(currentPlan)}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Invoices Table */}
      <div className="space-y-2">
        {filteredHistory?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucune facture pour {selectedYear}</p>
          </div>
        ) : (
          filteredHistory?.map((invoice) => (
            <div key={invoice?.id} className="border border-border rounded-lg overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer"
                onClick={() => toggleDetails(invoice?.id)}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                    <Icon name="Receipt" size={18} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-3">
                      <p className="font-medium text-card-foreground">
                        {invoice?.id}
                      </p>
                      {getStatusBadge(invoice?.status)}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>{formatDate(invoice?.date)}</span>
                      <span>•</span>
                      <span>Plan {getPlanName(invoice?.plan)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="font-semibold text-card-foreground">
                      {formatAmount(invoice?.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {invoice?.paymentMethod}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {invoice?.downloadUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDownload(invoice);
                        }}
                        title="Télécharger la facture"
                      />
                    )}
                    <Icon 
                      name={showDetails?.[invoice?.id] ? "ChevronUp" : "ChevronDown"} 
                      size={16} 
                      className="text-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {showDetails?.[invoice?.id] && (
                <div className="border-t border-border bg-muted/30 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-card-foreground">
                        Détails de la facture
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Numéro :</span>
                          <span className="text-card-foreground">{invoice?.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Période :</span>
                          <span className="text-card-foreground">{invoice?.period}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Montant HT :</span>
                          <span className="text-card-foreground">
                            {formatAmount(invoice?.amount * 0.8)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">TVA (20%) :</span>
                          <span className="text-card-foreground">
                            {formatAmount(invoice?.amount * 0.2)}
                          </span>
                        </div>
                        <div className="flex justify-between font-medium pt-1 border-t border-border">
                          <span className="text-card-foreground">Total TTC :</span>
                          <span className="text-card-foreground">
                            {formatAmount(invoice?.amount)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-card-foreground">
                        Informations de paiement
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Méthode :</span>
                          <span className="text-card-foreground">{invoice?.paymentMethod}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Statut :</span>
                          <span>{getStatusBadge(invoice?.status)}</span>
                        </div>
                        {invoice?.refundReason && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Remboursement :</span>
                            <span className="text-card-foreground text-xs">
                              {invoice?.refundReason}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {invoice?.downloadUrl && (
                        <div className="pt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            fullWidth
                            iconName="Download"
                            iconPosition="left"
                            iconSize={14}
                            onClick={() => handleDownload(invoice)}
                          >
                            Télécharger PDF
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {/* Help Section */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="HelpCircle" size={18} className="text-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-card-foreground mb-2">
                Besoin d'aide avec vos factures ?
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Notre équipe support est disponible pour vous aider avec toute question 
                concernant votre facturation ou vos paiements.
              </p>
              <Button
                variant="ghost"
                size="sm"
                iconName="MessageSquare"
                iconPosition="left"
                iconSize={14}
                onClick={() => console.log('Contact support')}
              >
                Contacter le support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;